import { OptionType } from '../enums/OptionType';
import Product from './Product.model';
import ProductOption from './ProductOption.model';

export interface OrderItem {
  id?: string; // Client-side tracking UUID
  VenueID?: string;
  CustomerID?: string;
  ProductID: string;
  Name: Record<string, string> | any;
  SID?: string;
  Quantity: number;
  ProductType?: number;
  OrderType?: number;
  OrderItemFeature: ProductOption[];
  Description?: string; // Item-specific note
  UnitBasePrice: number;
  UnitFeaturePrice?: number;
  UnitDiscountPrice?: number;
  UnitTaxablePrice?: number;
  OrderItemGrossAmount: number;
  OrderItemDiscountAmount?: number;
  OrderItemTaxableAmount?: number;
  OrderItemTaxPercentage?: number;
  OrderItemTaxAmount?: number;
  OrderItemTotalPayable: number;
  DefaultCurrencyID?: string;
  TableID?: string | null;
  FloorID?: string | null;
  product?: Product;
}

/**
 * Replicates bistronex-pos checkSetOrderItem:
 * Calculates UnitFeaturePrice, UnitTaxablePrice, OrderItemGrossAmount,
 * OrderItemDiscountAmount, OrderItemTaxableAmount, OrderItemTaxAmount,
 * and OrderItemTotalPayable based on OptionTypes.
 */
export function checkSetOrderItem(item: OrderItem): OrderItem {
  let featurePrice = 0;

  // 1. Calculate UnitFeaturePrice from ADD and PRODUCT options only
  if (item.OrderItemFeature && item.OrderItemFeature.length > 0) {
    for (const feature of item.OrderItemFeature) {
      const optType = typeof feature.Type === 'number' ? feature.Type : OptionType.ADD;
      if (optType === OptionType.ADD || optType === OptionType.PRODUCT) {
        const qty = typeof feature.Quantity === 'number' && feature.Quantity > 0 ? feature.Quantity : 1;
        const modifier = typeof feature.Modifier === 'number' ? feature.Modifier : 0;
        featurePrice += modifier * qty;
      }
    }
  }

  const unitBasePrice = typeof item.UnitBasePrice === 'number' ? item.UnitBasePrice : 0;
  item.UnitFeaturePrice = Number(featurePrice.toFixed(4));
  const grossUnitPrice = unitBasePrice + item.UnitFeaturePrice;

  // 2. Calculate discounts if DISCOUNT options exist
  let optionDiscount = 0;
  if (item.OrderItemFeature && item.OrderItemFeature.length > 0) {
    for (const feature of item.OrderItemFeature) {
      const optType = typeof feature.Type === 'number' ? feature.Type : OptionType.ADD;
      const modifier = typeof feature.Modifier === 'number' ? feature.Modifier : 0;
      const qty = typeof feature.Quantity === 'number' && feature.Quantity > 0 ? feature.Quantity : 1;

      if (optType === OptionType.DISCOUNT_PERCENTAGE) {
        optionDiscount += grossUnitPrice * (modifier / 100.0);
      } else if (optType === OptionType.DISCOUNT_AMOUNT) {
        optionDiscount += modifier * qty;
      }
    }
  }

  // Combine option discounts with any pre-existing unit discount, capped at grossUnitPrice
  const existingDiscount = typeof item.UnitDiscountPrice === 'number' &&
    !item.OrderItemFeature?.some(f => f.Type === OptionType.DISCOUNT_PERCENTAGE || f.Type === OptionType.DISCOUNT_AMOUNT)
    ? item.UnitDiscountPrice
    : 0;

  const totalDiscount = Math.min(grossUnitPrice, Math.max(0, optionDiscount + existingDiscount));
  item.UnitDiscountPrice = Number(totalDiscount.toFixed(4));

  const quantity = typeof item.Quantity === 'number' && item.Quantity > 0 ? item.Quantity : 1;
  const taxPercent = typeof item.OrderItemTaxPercentage === 'number' ? item.OrderItemTaxPercentage : 0;

  item.UnitTaxablePrice = Number(Math.max(0, grossUnitPrice - item.UnitDiscountPrice).toFixed(4));
  item.OrderItemGrossAmount = Number((grossUnitPrice * quantity).toFixed(2));
  item.OrderItemDiscountAmount = Number((item.UnitDiscountPrice * quantity).toFixed(2));
  item.OrderItemTaxableAmount = Number((item.UnitTaxablePrice * quantity).toFixed(2));
  item.OrderItemTaxPercentage = taxPercent;
  item.OrderItemTaxAmount = Number((item.OrderItemTaxableAmount * (taxPercent / 100)).toFixed(2));
  item.OrderItemTotalPayable = Number((item.OrderItemTaxableAmount + item.OrderItemTaxAmount).toFixed(2));

  return item;
}

export default OrderItem;
